import { useCallback, useEffect } from "react";

import clsx from "clsx";
import { saveAs } from "file-saver";
import { omit } from "lodash";
import Papa from "papaparse";

import { useOrderSummaryGraphKPILoading } from "@/atoms/order-page-atoms";
import { useUser } from "@components/user-context";
import { ChangePill } from "@components/v2/change-pill/change-pill";
import { useQueryMetadata } from "@components/v2/modals";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchOrderSummaryGraph,
  type FetchOrderSummaryGraphKPI,
  type FetchOrderSummaryGraphKPIVariables,
  type FetchOrderSummaryGraphVariables,
} from "@north-beam/nb-client-side";
import { MetricFormatters } from "@north-beam/nb-client-side/metrics";
import { metrics } from "@north-beam/nb-common";
import { dateFormat } from "@pages/orders/chart-legend";
import { type LegendMetric } from "@pages/sales-v2/components/chart";
import {
  FETCH_ORDER_SUMMARY_GRAPH,
  FETCH_ORDER_SUMMARY_GRAPH_KPI,
} from "@pages/settings/queries";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { useDateRange } from "./hooks/use-date-range";
import { useOrderGraphFilters } from "./hooks/use-orders-filters";

export function OrdersMetrics() {
  const { dateRange, ...restOfFilters } = useOrderGraphFilters();
  const { addQueryMetadata } = useQueryMetadata();

  const { dateRangeInterval, compareDateRangeInterval } = useDateRange();

  const [, setOrderQueryLoading] = useOrderSummaryGraphKPILoading();
  const { user } = useUser();

  const { data: graphResponse, loading } = useNorthbeamQuery<
    FetchOrderSummaryGraphKPI,
    FetchOrderSummaryGraphKPIVariables
  >(FETCH_ORDER_SUMMARY_GRAPH_KPI, {
    variables: {
      filterOptions: omit(restOfFilters, "granularity"),
      dateRange: dateRangeInterval,
      comparedDateRange: compareDateRangeInterval,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Orders",
        topic: "Orders KPI Metrics",
        metadata: data.me.orderSummaryGraphKPI.queryMetadata,
      });
    },
  });

  const currentKPIs = graphResponse?.me?.orderSummaryGraphKPI.currentKPIs;
  const comparisonKPIs = graphResponse?.me?.orderSummaryGraphKPI.comparisonKPIs;

  useEffect(() => {
    setOrderQueryLoading(loading);
  }, [loading, setOrderQueryLoading]);

  return (
    <ChartLegend
      legendMetrics={[
        {
          loading,
          currentValue: currentKPIs?.orderRevenue || 0,
          compareValue: comparisonKPIs?.orderRevenue || 0,
          name: "Revenue",
          isPositiveChangeGood: true,
          format: metrics.MetricTypes.CURRENCY,
          formatter: (v) =>
            MetricFormatters.currency(v, user.currencies.currentCurrency, {
              precision: "exact",
            }),
          hideComparisonFromTabDisplay: false,
        },
        {
          loading,
          currentValue: currentKPIs?.orderCount || 0,
          compareValue: comparisonKPIs?.orderCount || 0,
          name: "Orders",
          isPositiveChangeGood: true,
          format: metrics.MetricTypes.INTEGER,
          formatter: (v) =>
            MetricFormatters.integer(v, user.currencies.currentCurrency, {
              precision: "exact",
            }),
          hideComparisonFromTabDisplay: false,
        },
      ]}
      selectMetric={function (index: number): void {
        throw new Error("Function not implemented.");
      }}
      selectedMetricIndices={[]}
    />
  );
}

type ChartLegendProps = {
  legendMetrics: LegendMetric[];
  selectMetric: (index: number) => void;
  selectedMetricIndices: number[];
};

export const ChartLegend = ({ legendMetrics }: ChartLegendProps) => {
  return (
    <div>
      <div className="flex space-x-2">
        <div className="flex flex-wrap gap-4 items-center">
          {legendMetrics.map((metric) => {
            const {
              currentValue,
              compareValue,
              hideComparisonFromTabDisplay,

              isPositiveChangeGood,
            } = metric;

            const changeFraction = (currentValue - compareValue) / compareValue;

            return (
              <div key={metric.name} className={clsx("text-sm text-left")}>
                {metric.loading ? (
                  <div className="flex-center">
                    <Spinner />
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <div className="text-sm text-v2-secondary">
                      {metric.name}
                    </div>
                    <div
                      className={clsx(
                        "text-xs font-semibold font-mono flex items-center gap-2",
                      )}
                    >
                      <MetricValue
                        value={currentValue}
                        precision="exact"
                        as="span"
                        format={metric.format}
                      />
                      {!hideComparisonFromTabDisplay && (
                        <ChangePill
                          value={changeFraction ?? 0}
                          isPositiveChangeGood={isPositiveChangeGood}
                          size="md"
                          className="font-normal rounded-md"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center ">
          <OrderChartAddOns />
        </div>
      </div>
    </div>
  );
};

function OrderChartAddOns() {
  const { dateRange, granularity, ...restOfFilters } = useOrderGraphFilters();
  const { user } = useUser();
  const { addQueryMetadata } = useQueryMetadata();

  const { data: graphResponse, loading } = useNorthbeamQuery<
    FetchOrderSummaryGraph,
    FetchOrderSummaryGraphVariables
  >(FETCH_ORDER_SUMMARY_GRAPH, {
    variables: {
      filterOptions: omit(restOfFilters, "compareDateRange"),
      dateRange,
      granularity,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Orders",
        topic: "Orders Chart",
        metadata: data.me.orderSummaryGraph.queryMetadata,
      });
    },
  });

  // TODO: make this not hidden when Export is more well defined
  const onExport = useCallback(() => {
    const data = graphResponse?.me.orderSummaryGraph.data;
    if (!data) {
      return;
    }

    const headers = ["Date", "Revenue", "Transactions"];
    const rows = data.map((point) => {
      return [
        dateFormat(point.datetime, granularity, user.timezone),
        point.orderRevenue,
        point.orderCount,
      ];
    });
    const csvString = Papa.unparse([headers, ...rows]);

    const blob = new Blob([csvString], {
      type: "text/plain;charset=utf-8",
    });

    saveAs(blob, "orders-chart.csv");
  }, [granularity, graphResponse?.me.orderSummaryGraph.data, user.timezone]);

  return (
    <>
      {/* <SecondaryButton
        className="w-[10rem]"
        onClick={onExport}
        loading={loading}
      >
        EXPORT CHART DATA
      </SecondaryButton> */}
    </>
  );
}
